import React, { useState, useEffect } from "react";
import {
  Layout,
  Space,
  Table,
  Card,
  Flex,
  Tag,
  Button,
  Row,
  Col,
  Statistic,
  Typography,
  Input,
  Select,
  DatePicker,
  message,
  Modal,
  Descriptions,
  Alert,
  Tooltip,
  Badge,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  UserOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { getApi, postApi, moneyFormat, isSuperAdmin } from "@utils";
import { Header } from "@components";
import moment from "moment";

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Admin Stripe Accounts Management
export const AdminStripeAccounts = () => {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    search: "",
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [accountDetail, setAccountDetail] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin()) {
      window.location.href = "/";
      return;
    }
    fetchAccounts();
  }, [pagination.current, filters]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await getApi("getAdminStripeAccounts", {
        page: pagination.current,
        per_page: pagination.pageSize,
        status: filters.status,
        search: filters.search,
      });

      if (response?.data) {
        setAccounts(response.data);
        setStats(response.stats || {});
        setPagination((prev) => ({
          ...prev,
          total: response.count,
        }));
      }
    } catch (error) {
      message.error("Failed to load Stripe accounts");
    }
    setLoading(false);
  };

  const handleRefreshStatus = async (accountId) => {
    try {
      await postApi(`postAdminStripeAccountRefresh`, { id: accountId });
      message.success("Account status refreshed");
      fetchAccounts();
    } catch (error) {
      message.error("Failed to refresh account status");
    }
  };

  const handleResendOnboarding = async (accountId) => {
    try {
      await postApi(`postAdminStripeAccountResendOnboarding`, {
        id: accountId,
      });
      message.success("Onboarding email sent");
    } catch (error) {
      message.error("Failed to send onboarding email");
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      enabled: { color: "success", text: "Active" },
      pending: { color: "warning", text: "Pending" },
      restricted: { color: "error", text: "Restricted" },
      deauthorized: { color: "default", text: "Disconnected" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleViewDetails = async (record) => {
    setSelectedAccount(record);
    setDetailModalVisible(true);
    setDetailLoading(true);
    setAccountDetail(null);

    try {
      const response = await getApi(
        "getAdminStripeAccount",
        {},
        { id: record.id }
      );
      if (response?.data) {
        setAccountDetail(response.data);
      }
    } catch (error) {
      message.error("Failed to load account details");
    }
    setDetailLoading(false);
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      render: (user) => (
        <Flex gap={12} align="center">
          <UserOutlined />
          <div>
            <Text strong>{user.name || user.email}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {user.email}
            </Text>
          </div>
        </Flex>
      ),
    },
    {
      title: "Account ID",
      dataIndex: "stripe_account_id",
      render: (id) => (
        <Tooltip title={id}>
          <Text copyable={{ text: id }}>{id.substring(0, 14)}...</Text>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Onboarded",
      dataIndex: "fully_onboarded",
      render: (onboarded) =>
        onboarded ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Yes
          </Tag>
        ) : (
          <Tag color="orange" icon={<ClockCircleOutlined />}>
            No
          </Tag>
        ),
    },
    {
      title: "Country",
      dataIndex: "country",
      render: (country) => country || "-",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (date) => moment(date).format("MMM DD, YYYY"),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleViewDetails(record)}>
            Details
          </Button>
          {/* {!record.fully_onboarded && (
            <Button
              size="small"
              icon={<MailOutlined />}
              onClick={() => handleResendOnboarding(record.id)}
            >
              Send Onboarding
            </Button>
          )} */}
        </Space>
      ),
    },
  ];

  return (
    <Space
      direction="vertical"
      style={{ width: "100%", backgroundColor: "white" }}
    >
      <Header selected="admin_stripe_accounts" />
      <Content style={{ padding: "24px" }}>
        <Title level={4}>Stripe Accounts Management</Title>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Total Accounts"
                value={stats.total || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Active"
                value={stats.active || 0}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Pending"
                value={stats.pending || 0}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Restricted"
                value={stats.restricted || 0}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder="Search by email or name"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              onPressEnter={() => fetchAccounts()}
              style={{ width: 250 }}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: 150 }}
            >
              <Select.Option value="enabled">Active</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="restricted">Restricted</Select.Option>
            </Select>
            <Button onClick={() => fetchAccounts()} icon={<ReloadOutlined />}>
              Refresh
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={accounts}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) => {
                setPagination({ ...pagination, current: page, pageSize });
              },
            }}
          />
        </Card>

        <Modal
          title="Stripe Account Details"
          visible={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={null}
          width={700}
        >
          {detailLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <SyncOutlined spin style={{ fontSize: 24 }} />
              <div style={{ marginTop: 16 }}>Loading account details...</div>
            </div>
          ) : accountDetail ? (
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="User" span={2}>
                {selectedAccount.user.name || selectedAccount.user.email}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>
                {selectedAccount.user.email}
              </Descriptions.Item>
              <Descriptions.Item label="Account ID" span={2}>
                <Text copyable>{selectedAccount.stripe_account_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(selectedAccount.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {selectedAccount.account_type}
              </Descriptions.Item>
              <Descriptions.Item label="Country">
                {selectedAccount.country}
              </Descriptions.Item>
              <Descriptions.Item label="Currency">
                {selectedAccount.default_currency?.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Charges Enabled">
                {selectedAccount.charges_enabled ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="red">No</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Payouts Enabled">
                {selectedAccount.payouts_enabled ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="red">No</Tag>
                )}
              </Descriptions.Item>
              {selectedAccount.pending_requirements?.length > 0 && (
                <Descriptions.Item label="Pending Requirements" span={2}>
                  {selectedAccount.pending_requirements.map((req, idx) => (
                    <Tag key={idx} color="orange">
                      {req.replace(/_/g, " ")}
                    </Tag>
                  ))}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Transfer Stats" span={2}>
                <Space>
                  <Statistic
                    title="Total Transferred"
                    value={
                      accountDetail.account.transfer_stats?.total_transferred ||
                      0
                    }
                    prefix="$"
                    precision={2}
                    valueStyle={{ fontSize: 14 }}
                  />
                  <Statistic
                    title="Pending"
                    value={
                      accountDetail.account.transfer_stats?.total_pending || 0
                    }
                    prefix="$"
                    precision={2}
                    valueStyle={{ fontSize: 14 }}
                  />
                  <Statistic
                    title="Failed"
                    value={
                      accountDetail.account.transfer_stats?.total_failed || 0
                    }
                    prefix="$"
                    precision={2}
                    valueStyle={{ fontSize: 14 }}
                  />
                </Space>
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Modal>
      </Content>
    </Space>
  );
};

// Admin Stripe Transfers Management
export const AdminStripeTransfers = () => {
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    dateRange: null,
  });

  useEffect(() => {
    if (!isSuperAdmin()) {
      window.location.href = "/";
      return;
    }
    fetchTransfers();
  }, [pagination.current, filters]);

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        status: filters.status,
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.date_from = filters.dateRange[0].format("YYYY-MM-DD");
        params.date_to = filters.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await getApi("getAdminStripeTransfers", params);

      if (response?.data) {
        setTransfers(response.data);
        setStats(response.stats || {});
        setPagination((prev) => ({
          ...prev,
          total: response.count,
        }));
      }
    } catch (error) {
      message.error("Failed to load transfers");
    }
    setLoading(false);
  };

  const handleBulkTransfer = async () => {
    Modal.confirm({
      title: "Process Bulk Transfers",
      content: "This will process all confirmed payouts. Are you sure?",
      onOk: async () => {
        try {
          const response = await postApi("postAdminStripeTransfersBulk", {});
          message.success(
            `Processed: ${response.data.processed}, Failed: ${response.data.failed}, Skipped: ${response.data.skipped}`
          );
          fetchTransfers();
        } catch (error) {
          message.error("Failed to process bulk transfers");
        }
      },
    });
  };

  const handleRetry = async (transferId) => {
    try {
      await postApi(`postAdminTransferRetry`, { id: transferId });
      message.success("Transfer retry initiated");
      fetchTransfers();
    } catch (error) {
      message.error("Failed to retry transfer");
    }
  };

  const handleCancel = async (transferId) => {
    Modal.confirm({
      title: "Cancel Transfer",
      content: "Are you sure you want to cancel this transfer?",
      onOk: async () => {
        try {
          await postApi(`postAdminTransferCancel`, { id: transferId });
          message.success("Transfer canceled");
          fetchTransfers();
        } catch (error) {
          message.error("Failed to cancel transfer");
        }
      },
    });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: {
        color: "orange",
        icon: <ClockCircleOutlined />,
        text: "Pending",
      },
      in_transit: {
        color: "processing",
        icon: <SyncOutlined spin />,
        text: "In Transit",
      },
      paid: { color: "green", icon: <CheckCircleOutlined />, text: "Paid" },
      failed: {
        color: "red",
        icon: <ExclamationCircleOutlined />,
        text: "Failed",
      },
      canceled: {
        color: "default",
        icon: <ExclamationCircleOutlined />,
        text: "Canceled",
      },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: "User",
      dataIndex: "stripe_account",
      render: (account) => (
        <div>
          <Text strong>{account.user_email}</Text>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount) => <Text strong>{moneyFormat(amount)}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (date) => moment(date).format("MMM DD, YYYY HH:mm"),
    },
    {
      title: "Available",
      dataIndex: "available_on",
      render: (date) => (date ? moment(date).format("MMM DD, YYYY") : "-"),
    },
    {
      title: "Failure",
      dataIndex: "failure_message",
      render: (message) =>
        message ? (
          <Tooltip title={message}>
            <Text type="danger">View Error</Text>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.status === "failed" && (
            <Button
              size="small"
              type="primary"
              onClick={() => handleRetry(record.id)}
            >
              Retry
            </Button>
          )}
          {record.status === "pending" && (
            <Button size="small" danger onClick={() => handleCancel(record.id)}>
              Cancel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space
      direction="vertical"
      style={{ width: "100%", backgroundColor: "white" }}
    >
      <Header selected="admin_stripe_transfers" />
      <Content style={{ padding: "24px" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Title level={4}>Stripe Transfers Management</Title>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Pending"
                value={stats.pending_count || 0}
                suffix={`/ ${moneyFormat(stats.pending_amount || 0)}`}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Completed"
                value={stats.completed_count || 0}
                suffix={`/ ${moneyFormat(stats.completed_amount || 0)}`}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Failed"
                value={stats.failed_count || 0}
                suffix={`/ ${moneyFormat(stats.failed_amount || 0)}`}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={6}>
            <Card>
              <Statistic
                title="Total Volume"
                value={stats.completed_amount || 0}
                prefix="$"
                precision={2}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder="Filter by status"
              allowClear
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: 150 }}
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="in_transit">In Transit</Select.Option>
              <Select.Option value="paid">Paid</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="canceled">Canceled</Select.Option>
            </Select>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            />
          </Space>

          <Table
            columns={columns}
            dataSource={transfers}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) => {
                setPagination({ ...pagination, current: page, pageSize });
              },
            }}
          />
        </Card>
      </Content>
    </Space>
  );
};
